import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query'
import { casesApi } from '../api/casesApi'
import { Case } from '../../types'
import { toast } from 'react-toastify'

export const useCases = () => {
  const queryClient = useQueryClient()

  const query = useQuery({
    queryKey: ['cases'],
    queryFn: () => casesApi.getAll(),
  })

  const getByIdQuery = (id: string) => {
    return useQuery({
      queryKey: ['cases', id],
      queryFn: () => casesApi.getById(id),
    })
  }

  const createMutation = useMutation({
    mutationFn: (data: Omit<Case, 'id' | 'caseNumber' | 'createdAt' | 'updatedAt'>) =>
      casesApi.create(data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['cases'] })
      toast.success('Case created successfully')
    },
    onError: (error: any) => {
      toast.error(error.message || 'Failed to create case')
    },
  })

  const updateMutation = useMutation({
    mutationFn: ({ id, data }: { id: string; data: Partial<Case> }) =>
      casesApi.update(id, data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['cases'] })
      toast.success('Case updated successfully')
    },
    onError: (error: any) => {
      toast.error(error.message || 'Failed to update case')
    },
  })

  const deleteMutation = useMutation({
    mutationFn: (id: string) => casesApi.delete(id),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['cases'] })
      toast.success('Case deleted successfully')
    },
    onError: (error: any) => {
      toast.error(error.message || 'Failed to delete case')
    },
  })

  const searchMutation = useMutation({
    mutationFn: (query: string) => casesApi.search(query),
  })

  return {
    cases: query.data || [],
    isLoading: query.isLoading,
    error: query.error,
    refetch: query.refetch,
    getByIdQuery,
    createCase: createMutation.mutate,
    updateCase: updateMutation.mutate,
    deleteCase: deleteMutation.mutate,
    searchCases: searchMutation.mutate,
    searchResults: searchMutation.data || [],
    isSearching: searchMutation.isPending,
  }
}
